import { useEffect, useRef, useState } from "react";
import { useSaveDraft } from "@/lib/api/drafts";

const DRAFT_ELIGIBLE_TYPES = [
  "hld_wizard",
  "code_review",
  "hld",
  "refactor",
];

const DEBOUNCE_MS = 5000;

export type DraftStatus = "idle" | "saving" | "saved" | "error";

export function useDraftAutoSave(
  attemptId: number | null,
  questionId: number | null,
  questionType: string | undefined,
  answer: unknown | null,
) {
  const saveDraft = useSaveDraft();
  const [status, setStatus] = useState<DraftStatus>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>("");

  const isEligible =
    !!questionType && DRAFT_ELIGIBLE_TYPES.includes(questionType);

  // Store latest values in refs so the save function is stable
  const answerRef = useRef(answer);
  const attemptIdRef = useRef(attemptId);
  const questionIdRef = useRef(questionId);
  answerRef.current = answer;
  attemptIdRef.current = attemptId;
  questionIdRef.current = questionId;

  // Stable save function — doesn't change on every render
  const saveRef = useRef(() => {
    const a = answerRef.current;
    const aId = attemptIdRef.current;
    const qId = questionIdRef.current;
    if (!aId || !qId || a == null) return;

    const serialized = JSON.stringify(a);
    if (serialized === lastSavedRef.current) return;

    // Mark as saved immediately to prevent retries of the same content
    lastSavedRef.current = serialized;
    setStatus("saving");

    saveDraft.mutate(
      { attemptId: aId, questionId: qId, response: a },
      {
        onSuccess: () => setStatus("saved"),
        onError: () => {
          lastSavedRef.current = ""; // Reset so next attempt retries
          setStatus("error");
        },
      },
    );
  });
  // Keep the ref up to date with latest saveDraft instance
  saveRef.current = () => {
    const a = answerRef.current;
    const aId = attemptIdRef.current;
    const qId = questionIdRef.current;
    if (!aId || !qId || a == null) return;

    const serialized = JSON.stringify(a);
    if (serialized === lastSavedRef.current) return;

    lastSavedRef.current = serialized;
    setStatus("saving");

    saveDraft.mutate(
      { attemptId: aId, questionId: qId, response: a },
      {
        onSuccess: () => setStatus("saved"),
        onError: () => {
          lastSavedRef.current = ""; // Reset so next attempt retries
          setStatus("error");
        },
      },
    );
  };

  // Debounced save on answer change — only depends on serialized answer
  useEffect(() => {
    if (!isEligible || answer == null) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveRef.current(), DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [answer, isEligible]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        saveRef.current();
      }
    };
  }, []);

  return { status, isEligible };
}
